import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import {useTranslation} from "react-i18next";
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import moment from "moment-timezone";
import Button from "../../components/Button.jsx";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faRefresh} from "@fortawesome/free-solid-svg-icons/faRefresh";
import {faPlus} from "@fortawesome/free-solid-svg-icons/faPlus";
import LoadingComponent from "../../components/LoadingComponent.jsx";
import useAuthorityConfiguration from "../../api/utilities/useAuthorityConfiguration.js";
import toast from "react-hot-toast";
import Badge from "../../components/Badge.jsx";
import Pagination from "../../components/Pagination.jsx";
import {
    formatRuntimeDay,
    formatRuntimeDate,
    getProjectPhaseLabel,
    getRunModeLabel,
} from "../../components/runtime/runtimeStatusUtils.js";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import YesNo from "../../components/YesNo.jsx";
import {SystemApi} from "../../api/authority/generated/index";
import Tooltip from "../../components/Tooltip.jsx";
import CreateSchedulerSessionModal from "./CreateSchedulerSessionModal.jsx";

const SchedulerSessionTab = {
    all: "all",
    running: "running",
    scheduled: "scheduled",
    completed: "completed",
};

const autoRefreshIntervalMs = 15000;

export default function ServiceStatus() {
    const {t} = useTranslation();
    const {context} = useContext(GlobalContext);
    const authorityConfiguration = useAuthorityConfiguration();

    const basePath = authorityConfiguration.basePath;
    const authorizationHeader = authorityConfiguration.headers?.authorization ?? null;

    const [selectedTab, setSelectedTab] = useState(SchedulerSessionTab.all);
    const [selectedProjectId, setSelectedProjectId] = useState("");
    const [page, setPage] = useState(1);
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
    const [response, setResponse] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [expandedSessionIds, setExpandedSessionIds] = useState([]);
    const [recoverySession, setRecoverySession] = useState(null);
    const [isRecovering, setIsRecovering] = useState(false);
    const [createSessionOpen, setCreateSessionOpen] = useState(false);

    const requestIdRef = useRef(0);

    useEffect(() => {
        setPage(1);
        setExpandedSessionIds([]);
    }, [selectedProjectId, selectedTab]);

    const projectOptions = useMemo(() => {
        const availableProjects = [...(context.currentUserProjects ?? [])];
        availableProjects.sort((left, right) => left.name.localeCompare(right.name, "cs"));

        return [
            {
                value: "",
                label: t("runtime.scheduler.project_filter.all", {defaultValue: "Všechny projekty"}),
            },
            ...availableProjects.map((project) => ({
                value: project.id,
                label: project.name,
            })),
        ];
    }, [context.currentUserProjects, t]);

    const loadSessions = useCallback(async ({background = false} = {}) => {
        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;

        if (background) {
            setIsRefreshing(true);
        }
        else {
            setIsLoading(true);
        }

        try {
            const queryString = new URLSearchParams();
            queryString.set("state", selectedTab);
            queryString.set("page", String(page));

            if (selectedProjectId !== "") {
                queryString.set("projectId", selectedProjectId);
            }

            const fetchResponse = await fetch(`${basePath}/api/v1/queue/sessions?${queryString.toString()}`, {
                headers: {
                    Accept: "application/json",
                    ...(authorizationHeader ? {authorization: authorizationHeader} : {}),
                },
            });

            const parsedBody = await fetchResponse.json().catch(() => null);

            if (!fetchResponse.ok) {
                const errorMessage = parsedBody?.message
                    ?? t("runtime.scheduler.fetch_error", {defaultValue: "Nepodařilo se načíst scheduler sessions."});

                throw new Error(errorMessage);
            }

            if (requestId !== requestIdRef.current) {
                return;
            }

            setResponse(parsedBody);
        } catch (error) {
            if (requestId === requestIdRef.current) {
                toast.error(
                    error instanceof Error
                        ? error.message
                        : t("runtime.scheduler.fetch_error", {defaultValue: "Nepodařilo se načíst scheduler sessions."}),
                );
            }
        } finally {
            if (requestId === requestIdRef.current) {
                setIsLoading(false);
                setIsRefreshing(false);
            }
        }
    }, [authorizationHeader, basePath, page, selectedProjectId, selectedTab, t]);

    useEffect(() => {
        void loadSessions();
    }, [loadSessions]);

    useEffect(() => {
        if (!autoRefreshEnabled) {
            return undefined;
        }

        const intervalId = setInterval(() => {
            void loadSessions({background: true});
        }, autoRefreshIntervalMs);

        return () => {
            clearInterval(intervalId);
        };
    }, [autoRefreshEnabled, loadSessions]);

    const items = response?.data ?? [];
    const pagination = response?.pagination;
    const totalCount = pagination?.itemCount ?? items.length;

    const summaryBadges = useMemo(() => [
        {
            key: SchedulerSessionTab.all,
            color: "slate",
            label: t("runtime.scheduler.tabs.all", {defaultValue: "Vše"}),
        },
        {
            key: SchedulerSessionTab.running,
            color: "yellow",
            label: t("runtime.scheduler.tabs.running", {defaultValue: "Běží"}),
        },
        {
            key: SchedulerSessionTab.scheduled,
            color: "blue",
            label: t("runtime.scheduler.tabs.scheduled", {defaultValue: "Naplánováno"}),
        },
        {
            key: SchedulerSessionTab.completed,
            color: "emerald",
            label: t("runtime.scheduler.tabs.completed", {defaultValue: "Dokončeno"}),
        },
    ], [t]);

    function toggleExpanded(sessionId) {
        setExpandedSessionIds((currentExpandedSessionIds) => {
            if (currentExpandedSessionIds.includes(sessionId)) {
                return currentExpandedSessionIds.filter((currentSessionId) => currentSessionId !== sessionId);
            }

            return [...currentExpandedSessionIds, sessionId];
        });
    }

    const recoverStuckSession = useCallback(async () => {
        if (recoverySession === null) {
            return;
        }

        setIsRecovering(true);

        try {
            await new SystemApi(authorityConfiguration).recoverStuckSchedulerSession({
                sessionId: recoverySession.id,
            });

            toast.success(t("runtime.scheduler.recovery_success", {
                defaultValue: "Session byla předána k dokončení a přepočtu.",
            }));
            setRecoverySession(null);
            await loadSessions({background: true});
        } catch (error) {
            toast.error(error instanceof Error ? error.message : t("runtime.scheduler.recovery_error", {
                defaultValue: "Nepodařilo se předat session k dokončení.",
            }));
        } finally {
            setIsRecovering(false);
        }
    }, [authorityConfiguration, loadSessions, recoverySession, t]);

    return (
        <WideSecureLayout
            title={
                <>
                    <span>{t("route.service-status")}</span>
                    <Button variant={"violet"} size={"small"} onClick={() => setCreateSessionOpen(true)}>
                        <FontAwesomeIcon icon={faPlus} className={"mr-2"}/>
                        {t("runtime.scheduler.create.open", {defaultValue: "Nový fetch"})}
                    </Button>
                </>
            }
            actions={
                <div className={"flex flex-wrap items-center justify-center gap-3 md:justify-end"}>
                    <label className={"inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"}>
                        <input
                            type={"checkbox"}
                            className={"rounded border-slate-300 text-slate-900 focus:ring-slate-400"}
                            checked={autoRefreshEnabled}
                            onChange={(event) => setAutoRefreshEnabled(event.target.checked)}
                        />
                        {t("runtime.scheduler.auto_refresh", {defaultValue: "Automaticky obnovovat"})}
                    </label>

                    <Button variant={"outline-primary"} onClick={() => void loadSessions({background: response !== null})}>
                        <FontAwesomeIcon icon={faRefresh}/>
                    </Button>
                </div>
            }
        >
            <div className={"space-y-6"}>
                <YesNo
                    visible={recoverySession !== null}
                    setVisible={(visible) => {
                        if (!visible && !isRecovering) {
                            setRecoverySession(null);
                        }
                    }}
                    title={t("runtime.scheduler.recovery_confirm_title", {defaultValue: "Dokončit zaseknutou session?"})}
                    question={t("runtime.scheduler.recovery_confirm_question", {
                        defaultValue: "Nedokončené joby budou označeny jako chybné. Session pak worker dokončí standardním párováním a přepočtem. Použijte pouze pokud už původní PHP proces neběží.\n\nSession ID: {{sessionId}}",
                        sessionId: recoverySession?.id,
                    })}
                    onYes={() => void recoverStuckSession()}
                    loading={isRecovering}
                />
                <CreateSchedulerSessionModal
                    visible={createSessionOpen}
                    setVisible={setCreateSessionOpen}
                    projects={context.currentUserProjects ?? []}
                    onCreated={() => loadSessions({background: true})}
                />
                <div className={"flex flex-wrap items-center gap-3"}>
                    {summaryBadges.map((summaryBadge) => (
                        <button
                            key={summaryBadge.key}
                            type={"button"}
                            onClick={() => setSelectedTab(summaryBadge.key)}
                            className={"rounded-full"}
                        >
                            <Badge
                                color={summaryBadge.color}
                                className={'px-2 py-1.5' + (selectedTab === summaryBadge.key ? ' ring-1 ring-slate-500' : '')}
                            >
                                {summaryBadge.label}
                            </Badge>
                        </button>
                    ))}

                    <div className={"text-sm text-slate-500 dark:text-slate-400"}>
                        <small>
                        {t("runtime.scheduler.total", {
                            defaultValue: "Celkem: {{count}}",
                            count: totalCount,
                        })}
                        </small>
                    </div>

                    {isRefreshing && (
                        <div className={"text-sm text-slate-500 dark:text-slate-400"}>
                            {t("runtime.scheduler.refreshing", {defaultValue: "Obnovuji..."})}
                        </div>
                    )}

                    <div className={"min-w-64 ml-auto"}>
                        <select
                            className={"form-select w-full"}
                            value={selectedProjectId}
                            onChange={(event) => setSelectedProjectId(event.target.value)}
                        >
                            {projectOptions.map((projectOption) => (
                                <option key={projectOption.value || "all-projects"} value={projectOption.value}>
                                    {projectOption.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <LoadingComponent loading={isLoading && response !== null} className={"rounded-2xl"}>
                    {response === null && isLoading ? (
                        <div className={"rounded-2xl border border-slate-200 bg-white p-8 dark:border-slate-700 dark:bg-slate-800"}>
                            <LoadingComponent loading={true} className={"min-h-48"} loadedClassName={"min-h-48"}/>
                        </div>
                    ) : (
                        <div className={"overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800"}>
                            <div className={"overflow-x-auto"}>
                                <table className={"min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-700"}>
                                    <thead className={"bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900/40 dark:text-slate-400"}>
                                    <tr>
                                        <th className={"px-4 py-3"}>{t("runtime.scheduler.project", {defaultValue: "Projekt"})}</th>
                                        <th className={"px-4 py-3"}>{t("runtime.scheduler.state", {defaultValue: "Stav"})}</th>
                                        <th className={"px-4 py-3"}>{t("runtime.scheduler.period", {defaultValue: "Období"})}</th>
                                        <th className={"px-4 py-3"}>{t("runtime.scheduler.planned_for", {defaultValue: "Naplánováno"})}</th>
                                        <th className={"px-4 py-3"}>{t("runtime.scheduler.started_at", {defaultValue: "Začalo"})}</th>
                                        <th className={"px-4 py-3"}>{t("runtime.scheduler.finished_at", {defaultValue: "Skončilo"})}</th>
                                        <th className={"px-4 py-3"}>{t("runtime.scheduler.error", {defaultValue: "Chyba"})}</th>
                                        <th className={"w-16 px-4 py-3"}>{t("runtime.scheduler.details", {defaultValue: "Detail"})}</th>
                                    </tr>
                                    </thead>
                                    <tbody className={"divide-y divide-slate-200 dark:divide-slate-700"}>
                                    {items.length === 0 && (
                                        <tr>
                                            <td colSpan={8} className={"px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400"}>
                                                {t("runtime.scheduler.empty", {defaultValue: "Pro vybraný filtr tu teď nic není."})}
                                            </td>
                                        </tr>
                                    )}

                                    {items.map((session) => {
                                        const isExpanded = expandedSessionIds.includes(session.id);
                                        const isExpandable = session.kind !== "historical_precalculation" && session.jobs.length > 0;

                                        return (
                                            <FragmentSessionRows
                                                key={session.id}
                                                session={session}
                                                isExpanded={isExpanded}
                                                isExpandable={isExpandable}
                                                onToggleExpanded={toggleExpanded}
                                                onRecover={() => setRecoverySession(session)}
                                                t={t}
                                            />
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </LoadingComponent>

                {pagination && pagination.pageCount > 1 && (
                    <Pagination
                        pagination={pagination}
                        onPageChange={(nextPage) => setPage(nextPage)}
                    />
                )}
            </div>
        </WideSecureLayout>
    );
}

function FragmentSessionRows({session, isExpanded, isExpandable, onToggleExpanded, onRecover, t}) {
    const isRecoverable = session.startedAt !== null
        && ["pending", "fetching", "pairing", "building", "publishing"].includes(session.status)
        && moment(session.startedAt).add(30, "minutes").isSameOrBefore(moment());

    return (
        <>
            <tr className={session.hasErrors ? "bg-rose-50/60 dark:bg-rose-950/10" : ""}>
                <td className={"px-4 py-3 align-top"}>
                    <div className={"font-medium text-black dark:text-slate-100"}>{session.projectName}</div>
                    <div className={"mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400"}>
                        <Badge color={"violet"} className={"px-1.5 py-0"}>{getRunModeLabel(t, session.mode, session.kind)}</Badge>
                        <JobProgress jobs={session.jobs} t={t}/>
                        <TechnicalIdentifier shortLabel={"PID"} label={t("runtime.scheduler.project_id", {defaultValue: "Project ID"})} value={session.projectId} t={t}/>
                        <TechnicalIdentifier shortLabel={"SID"} label={t("runtime.scheduler.session_id", {defaultValue: "Session ID"})} value={session.id} t={t}/>
                    </div>
                </td>

                <td className={"px-4 py-3 align-top"}>
                    <SessionStatusBadge status={session.status} hasErrors={session.hasErrors} t={t}/>
                </td>

                <td className={"px-4 py-3 align-top"}>
                    <DateRangeCell since={session.since} until={session.until}/>
                </td>

                <td className={"px-4 py-3 align-top"}>
                    <DateCell value={session.plannedFor}/>
                </td>

                <td className={"px-4 py-3 align-top"}>
                    <DateCell value={session.startedAt}/>
                </td>

                <td className={"px-4 py-3 align-top"}>
                    <DateCell value={session.finishedAt}/>
                </td>

                <td className={"px-4 py-3 align-top"}>
                    {session.hasErrors ? (
                        <div className={"space-y-2"}>
                            <Badge color={"rose"}>
                                {t("runtime.scheduler.error_badge", {defaultValue: "Chyba"})}
                            </Badge>
                            {session.errorMessage && (
                                <RuntimeFailureMessage technicalDetail={session.errorMessage} t={t}/>
                            )}
                        </div>
                    ) : (
                        <span className={"text-xs text-slate-400 dark:text-slate-500"}>—</span>
                    )}
                </td>

                <td className={"px-4 py-3 align-top"}>
                    <div className={"flex flex-col items-start gap-2"}>
                        {isExpandable && (
                            <Button
                                variant={"outline-black"}
                                size={"small"}
                                onClick={() => onToggleExpanded(session.id)}
                            >
                                {isExpanded
                                    ? t("runtime.scheduler.collapse", {defaultValue: "Sbalit"})
                                    : t("runtime.scheduler.expand", {defaultValue: "Detail"})}
                            </Button>
                        )}
                        {isRecoverable && (
                            <Button variant={"outline-red"} size={"small"} onClick={onRecover}>
                                {t("runtime.scheduler.recover", {defaultValue: "Dokončit"})}
                            </Button>
                        )}
                    </div>
                </td>
            </tr>

            {isExpanded && (
                <tr className={"bg-slate-50/80 dark:bg-slate-900/30"}>
                    <td colSpan={8} className={"px-4 py-3"}>
                        <SchedulerSessionJobsTable jobs={session.jobs} t={t}/>
                    </td>
                </tr>
            )}
        </>
    );
}

function JobProgress({jobs, t}) {
    if (jobs.length === 0) {
        return null;
    }

    const doneCount = jobs.filter((job) => job.status === "done").length;
    const failedCount = jobs.filter((job) => job.status === "failed").length;
    const processingCount = jobs.filter((job) => job.status === "processing").length;
    const pendingCount = jobs.filter((job) => job.status === "pending").length;
    const skippedCount = jobs.filter((job) => job.status === "skipped").length;
    const progressColor = failedCount > 0 ? "rose" : (processingCount > 0 || pendingCount > 0 ? "yellow" : "emerald");

    return (
        <span className={"inline-flex flex-wrap items-center gap-1"}>
            <Badge color={progressColor} className={"px-1.5 py-0"}>
                {t("runtime.scheduler.job_progress.completed", {
                    defaultValue: "{{done}} / {{total}} hotovo",
                    done: doneCount,
                    total: jobs.length,
                })}
            </Badge>
            {failedCount > 0 && (
                <Badge color={"rose"} className={"px-1.5 py-0"}>
                    {t("runtime.scheduler.job_progress.failed", {defaultValue: "{{count}} chyba", count: failedCount})}
                </Badge>
            )}
            {processingCount > 0 && (
                <Badge color={"yellow"} className={"px-1.5 py-0"}>
                    {t("runtime.scheduler.job_progress.processing", {defaultValue: "{{count}} běží", count: processingCount})}
                </Badge>
            )}
            {pendingCount > 0 && (
                <Badge color={"blue"} className={"px-1.5 py-0"}>
                    {t("runtime.scheduler.job_progress.pending", {defaultValue: "{{count}} čeká", count: pendingCount})}
                </Badge>
            )}
            {skippedCount > 0 && (
                <Badge color={"slate"} className={"px-1.5 py-0"}>
                    {t("runtime.scheduler.job_progress.skipped", {defaultValue: "{{count}} přeskočeno", count: skippedCount})}
                </Badge>
            )}
        </span>
    );
}

function SessionKindBadge({kind, t}) {
    if (kind === "historical_precalculation") {
        return (
            <Badge color={"blue"}>
                {t("runtime.scheduler.kind.historical_precalculation", {defaultValue: "Historický přepočet"})}
            </Badge>
        );
    }

    return (
        <Badge color={"slate"}>
            {t("runtime.scheduler.kind.standard", {defaultValue: "Basic session"})}
        </Badge>
    );
}

function SessionStatusBadge({status, hasErrors, t}) {
    if (status === "done") {
        if (hasErrors) {
            return <Badge color={"rose"}>{t("runtime.scheduler.status.done_with_errors", {defaultValue: "Dokončeno s chybami"})}</Badge>;
        }
        return <Badge color={"emerald"}>{t("runtime.scheduler.status.done", {defaultValue: "Dokončeno"})}</Badge>;
    }

    if (status === "failed") {
        return <Badge color={"rose"}>{t("runtime.scheduler.status.failed", {defaultValue: "Fail"})}</Badge>;
    }

    if (status === "waiting" || status === "pending") {
        return <Badge color={"blue"}>{t("runtime.scheduler.status.scheduled", {defaultValue: "Naplánováno"})}</Badge>;
    }

    return (
        <Badge color={"yellow"}>
            {getProjectPhaseLabel(t, status) ?? t("runtime.scheduler.status.running", {defaultValue: "Běží"})}
        </Badge>
    );
}

function JobStatusBadge({status, t}) {
    switch (status) {
        case "done":
            return <Badge color={"emerald"}>{t("runtime.scheduler.job.done", {defaultValue: "Dokončeno"})}</Badge>;
        case "failed":
            return <Badge color={"rose"}>{t("runtime.scheduler.job.failed", {defaultValue: "Fail"})}</Badge>;
        case "processing":
            return <Badge color={"yellow"}>{t("runtime.scheduler.job.processing", {defaultValue: "Běží"})}</Badge>;
        case "skipped":
            return <Badge color={"slate"}>{t("runtime.scheduler.job.skipped", {defaultValue: "Přeskočeno"})}</Badge>;
        default:
            return <Badge color={"slate"}>{t("runtime.scheduler.job.pending", {defaultValue: "Čeká"})}</Badge>;
    }
}

function SchedulerSessionJobsTable({jobs, t}) {
    return (
        <div className={"overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800"}>
            <div className={"overflow-x-auto"}>
                <table className={"min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-700"}>
                    <thead className={"bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900/40 dark:text-slate-400"}>
                    <tr>
                        <th className={"px-4 py-3"}>{t("runtime.scheduler.integration", {defaultValue: "Integrace"})}</th>
                        <th className={"px-4 py-3"}>{t("runtime.scheduler.state", {defaultValue: "Stav"})}</th>
                        <th className={"px-4 py-3"}>{t("runtime.scheduler.started_at", {defaultValue: "Začalo"})}</th>
                        <th className={"px-4 py-3"}>{t("runtime.scheduler.finished_at", {defaultValue: "Skončilo"})}</th>
                        <th className={"px-4 py-3"}>{t("runtime.scheduler.error", {defaultValue: "Chyba"})}</th>
                    </tr>
                    </thead>
                    <tbody className={"divide-y divide-slate-200 dark:divide-slate-700"}>
                    {jobs.map((job) => (
                        <tr key={job.id}>
                            <td className={"px-4 py-2.5 align-top"}>
                                <div className={"font-medium text-slate-900 dark:text-slate-100"}>{job.integrationName}</div>
                                <div className={"mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400"}>
                                    <TechnicalIdentifier shortLabel={"IID"} label={t("runtime.scheduler.integration_id", {defaultValue: "Integration ID"})} value={job.integrationId} t={t}/>
                                    <TechnicalIdentifier shortLabel={"JID"} label={t("runtime.scheduler.job_id", {defaultValue: "Job ID"})} value={job.id} t={t}/>
                                </div>
                            </td>
                            <td className={"px-4 py-2.5 align-top"}>
                                <JobStatusBadge status={job.status} t={t}/>
                            </td>
                            <td className={"px-4 py-2.5 align-top"}>
                                <DateCell value={job.startedAt}/>
                            </td>
                            <td className={"px-4 py-2.5 align-top"}>
                                <DateCell value={job.finishedAt}/>
                            </td>
                            <td className={"px-4 py-2.5 align-top"}>
                                {job.failureMessage ? (
                                    <RuntimeFailureMessage technicalDetail={job.failureMessage} t={t}/>
                                ) : (
                                    <span className={"text-xs text-slate-400 dark:text-slate-500"}>—</span>
                                )}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function RuntimeFailureMessage({technicalDetail, t}) {
    return (
        <div className={"max-w-md space-y-2 text-xs text-rose-700 dark:text-rose-300"}>
            <div>
                {t("runtime.scheduler.safe_failure_message", {
                    defaultValue: "Data se nepodařilo aktualizovat. Zkontrolujte stav připojení a přístup ke zdroji dat.",
                })}
            </div>
            <details className={"rounded-md border border-slate-300 bg-white/70 px-2.5 py-2 text-slate-700 dark:border-slate-600 dark:bg-slate-900/40 dark:text-slate-200"}>
                <summary className={"cursor-pointer font-semibold"}>
                    {t("runtime.scheduler.technical_detail", {defaultValue: "Technický detail"})}
                </summary>
                <div className={"mt-2 whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed"}>
                    {technicalDetail}
                </div>
            </details>
        </div>
    );
}

function TechnicalIdentifier({shortLabel, label, value, t}) {
    const shortenedValue = `${value.slice(0, 8)}…${value.slice(-6)}`;

    async function copyValue() {
        try {
            await navigator.clipboard.writeText(value);
            toast.success(t("copy-to-clipboard-success"));
        } catch {
            toast.error(t("copy-to-clipboard-failed"));
        }
    }

    return (
        <span className={"inline-flex items-center gap-1 whitespace-nowrap"}>
            <span className={"text-[10px] font-bold uppercase tracking-wide text-slate-900 dark:text-slate-100"}>{shortLabel}:</span>
            <Tooltip
                bg={"dark"}
                size={"md"}
                position={"bottom"}
                className={"inline-flex"}
                trigger={<span className={"font-mono text-[11px] text-slate-500 dark:text-slate-400"}>{shortenedValue}</span>}
                onClick={() => void copyValue()}
            >
                <div className={"font-mono text-xs break-all"}>{label}: {value}</div>
            </Tooltip>
        </span>
    );
}

function DateCell({value}) {
    const formattedValue = useMemo(() => {
        if (!value) {
            return null;
        }

        return formatRuntimeDate(value) ?? moment(value).format("DD.MM.YYYY HH:mm");
    }, [value]);

    if (!formattedValue) {
        return <span className={"text-xs text-slate-400 dark:text-slate-500"}>—</span>;
    }

    return <span className={"text-slate-700 dark:text-slate-200"}>{formattedValue}</span>;
}

function DateRangeCell({since, until}) {
    if (!since || !until) {
        return <span className={"text-xs text-slate-400 dark:text-slate-500"}>—</span>;
    }

    return (
        <span className={"whitespace-nowrap text-slate-700 dark:text-slate-200"}>
            {formatRuntimeDay(since)} – {formatRuntimeDay(until)}
        </span>
    );
}
